// See Issue #167
import { useState, useEffect } from 'react';
import { featureFlagApi } from '../../services/api';
import { FeatureFlag, FeatureFlagCreate, FeatureFlagUpdate } from '../../types';
import { Flag, Plus, RefreshCw, Trash2, Edit2, AlertCircle, XCircle } from 'lucide-react';
import { extractApiError } from '../../utils/errorUtils'; // Issue #964: Standardized error handling

/**
 * Feature Flag Management Component (Issue #167)
 *
 * Admin interface for managing database-backed feature flags.
 * Enables runtime feature toggling without code deployments.
 */
const FeatureFlagManagement = () => {
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState<FeatureFlag | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Form state
  const [formData, setFormData] = useState<FeatureFlagCreate>({
    flag_key: '',
    flag_name: '',
    description: '',
    is_enabled: false,
  });

  useEffect(() => {
    loadFlags();
  }, []);

  const loadFlags = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await featureFlagApi.getAllFlags();
      setFlags(response.items);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Error loading flags:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (flag: FeatureFlag) => {
    try {
      const updateData: FeatureFlagUpdate = {
        is_enabled: !flag.is_enabled,
      };
      await featureFlagApi.updateFlag(flag.flag_key, updateData);
      await loadFlags();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Error toggling flag:', err);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError(null);
      await featureFlagApi.createFlag(formData);
      setShowCreateModal(false);
      setFormData({
        flag_key: '',
        flag_name: '',
        description: '',
        is_enabled: false,
      });
      await loadFlags();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Error creating flag:', err);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFlag) return;

    try {
      setError(null);
      const updateData: FeatureFlagUpdate = {
        is_enabled: selectedFlag.is_enabled,
        flag_name: formData.flag_name,
        description: formData.description,
      };
      await featureFlagApi.updateFlag(selectedFlag.flag_key, updateData);
      setShowEditModal(false);
      setSelectedFlag(null);
      await loadFlags();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Error updating flag:', err);
    }
  };

  const handleDelete = async () => {
    if (!selectedFlag) return;

    try {
      setError(null);
      await featureFlagApi.deleteFlag(selectedFlag.flag_key);
      setShowDeleteConfirm(false);
      setSelectedFlag(null);
      await loadFlags();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Error deleting flag:', err);
    }
  };

  const openEditModal = (flag: FeatureFlag) => {
    setSelectedFlag(flag);
    setFormData({
      flag_key: flag.flag_key,
      flag_name: flag.flag_name,
      description: flag.description || '',
      is_enabled: flag.is_enabled,
    });
    setShowEditModal(true);
  };

  const openDeleteConfirm = (flag: FeatureFlag) => {
    setSelectedFlag(flag);
    setShowDeleteConfirm(true);
  };

  const filteredFlags = flags.filter(flag =>
    flag.flag_key.toLowerCase().includes(searchTerm.toLowerCase()) ||
    flag.flag_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="w-full px-1">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Flag className="w-6 h-6 text-teal-600" />
            <h1 className="text-2xl font-bold text-gray-900">Feature Flags</h1>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={loadFlags}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg flex items-center space-x-2"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Create Flag</span>
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="mt-4">
          <input
            type="text"
            placeholder="Search flags by key or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-800">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-600 hover:text-red-800"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Flags Table */}
      <div className="px-6 py-4">
        <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Flag Key
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredFlags.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    {searchTerm ? 'No flags match your search' : 'No feature flags yet. Create one to get started!'}
                  </td>
                </tr>
              ) : (
                filteredFlags.map((flag) => (
                  <tr key={flag.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggle(flag)}
                        className="relative inline-flex items-center"
                      >
                        <div className={`w-12 h-6 rounded-full transition-colors ${
                          flag.is_enabled ? 'bg-green-600' : 'bg-gray-300'
                        }`}>
                          <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                            flag.is_enabled ? 'translate-x-6' : 'translate-x-0.5'
                          } mt-0.5`} />
                        </div>
                        <span className={`ml-3 text-sm font-medium ${
                          flag.is_enabled ? 'text-green-700' : 'text-gray-500'
                        }`}>
                          {flag.is_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <code className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-mono">
                        {flag.flag_key}
                      </code>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{flag.flag_name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 max-w-md truncate">
                        {flag.description || <span className="italic">No description</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button
                        onClick={() => openEditModal(flag)}
                        className="text-teal-600 hover:text-teal-900"
                      >
                        <Edit2 className="w-4 h-4 inline" />
                      </button>
                      <button
                        onClick={() => openDeleteConfirm(flag)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                <Plus className="w-5 h-5" />
                <span>Create Feature Flag</span>
              </h2>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Flag Key <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.flag_key}
                    onChange={(e) => setFormData({ ...formData, flag_key: e.target.value })}
                    placeholder="e.g., stripe_payments"
                    pattern="[a-z0-9_-]+"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">Lowercase letters, numbers, underscores, and hyphens only</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Flag Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.flag_name}
                    onChange={(e) => setFormData({ ...formData, flag_name: e.target.value })}
                    placeholder="e.g., Stripe Payment Integration"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe what this feature flag controls..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_enabled"
                    checked={formData.is_enabled}
                    onChange={(e) => setFormData({ ...formData, is_enabled: e.target.checked })}
                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="is_enabled" className="text-sm font-medium text-gray-700">
                    Enable immediately
                  </label>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setFormData({
                        flag_key: '',
                        flag_name: '',
                        description: '',
                        is_enabled: false,
                      });
                    }}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                  >
                    Create Flag
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedFlag && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                <Edit2 className="w-5 h-5" />
                <span>Edit Feature Flag</span>
              </h2>
              <form onSubmit={handleUpdate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Flag Key
                  </label>
                  <input
                    type="text"
                    value={selectedFlag.flag_key}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">Flag key cannot be changed</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Flag Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.flag_name}
                    onChange={(e) => setFormData({ ...formData, flag_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedFlag(null);
                    }}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedFlag && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Delete Feature Flag?
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to delete <strong>{selectedFlag.flag_name}</strong> ({selectedFlag.flag_key})?
                  This action cannot be undone.
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setSelectedFlag(null);
                    }}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Delete Flag
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureFlagManagement;
